import type { JSX } from 'react'
import { cn } from '@/lib/cn'

/**
 * Линейный индикатор с порогами: загрузка runner'а (боты, CPU, память).
 *
 * Линейный, а не круговой: рядом их три и они сравниваются между собой — длину
 * глаз сопоставляет точнее, чем угол. Цвет меняется на пороге, но он не
 * единственный сигнал: рядом всегда стоит число, а сам элемент —
 * `role="meter"` со `aria-valuenow`.
 */
export interface MeterProps {
  label: string
  /** Текущее значение в тех же единицах, что `max`. */
  value: number
  max: number
  /** Готовая подпись значения: «3 / 16», «41 %», «182 МБ». */
  text: string
  /** Доли от max, после которых индикатор желтеет и краснеет. */
  thresholds?: { warn: number; danger: number }
  className?: string
}

export function Meter({
  label,
  value,
  max,
  text,
  thresholds = { warn: 0.75, danger: 0.9 },
  className
}: MeterProps): JSX.Element {
  const safeMax = max > 0 ? max : 1
  const ratio = Math.max(0, Math.min(1, value / safeMax))
  const tone =
    ratio >= thresholds.danger ? 'bg-fail' : ratio >= thresholds.warn ? 'bg-warn' : 'bg-live'

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-ink-muted">{label}</span>
        <span className="font-mono text-xs font-medium text-ink tabular-nums">{text}</span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuenow={Math.round(value * 100) / 100}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuetext={text}
        className="h-1.5 overflow-hidden rounded-full bg-panel-3"
      >
        {/* Ширина через transform: анимация полосы не заставляет пересчитывать
            раскладку соседей (docs/10 §1.4). */}
        <span
          className={cn('block h-full origin-left rounded-full', tone)}
          style={{
            width: '100%',
            transform: `scaleX(${ratio})`,
            transition: 'transform var(--duration-chart) var(--ease-spring)'
          }}
        />
      </div>
    </div>
  )
}
